import React, { useEffect, useState } from 'react';
import { StarIcon, ArrowLeftIcon, LockClosedIcon, TrophyIcon, AcademicCapIcon, SparklesIcon, CheckCircleIcon, XCircleIcon, ShareIcon, XMarkIcon } from '@heroicons/react/24/solid';
import axios from 'axios';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { format } from 'date-fns'; // Import format for date formatting
import baseUrl from '../../config';

// IMPORTANT: Replace this with your actual API base URL.

const AchievementsPage = () => {
  const [unlockedBatches, setUnlockedBatches] = useState([]);
  const [lockedBatches, setLockedBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null); // State for the clicked batch
  const [celebrationVisible, setCelebrationVisible] = useState(false); // State to control celebration visibility
  const [studentData, setStudentData] = useState(null); // To store full student data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userOverallProgress, setUserOverallProgress] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth); // State for window width
  const [windowHeight, setWindowHeight] = useState(window.innerHeight); // State for window height

  const navigate = useNavigate(); // Initialize navigate hook

  // Helper function to get badge image/placeholder
  const getBatchImage = (batchId) => {
    try {
      return require(`../../Assets/batch/${batchId}.png`);
    } catch (error) {
      // Fallback for missing images – a generic icon or placeholder
      return `https://api.dicebear.com/7.x/shapes/svg?seed=${batchId}&colors=emerald,yellow,indigo`; // Fun placeholder!
    }
  };
  // Function to define unlock criteria for locked badges.
  // In a real application, this data would ideally come from your API.
  const getUnlockCriteria = (batchId) => {
    switch (batchId) {
      case 'batchB': return 'Solve 10 coding challenges in JavaScript.';
      case 'batchC': return 'Participate in 3 community discussions.';
      case 'batchE': return 'Complete the Data Science track.';
      case 'batchH': return 'Pass AWS Certified Developer exam.';
      default: return 'Explore more courses and challenges!';
    }
  };

  // Function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return format(date, 'MMMM dd, yyyy');
    } catch (e) {
      console.error("Error formatting date:", e);
      return 'Invalid Date';
    }
  };

  // Handle click on an unlocked badge to show celebration
  const handleBatchClick = (batch) => {
    setSelectedBatch(batch);
    setCelebrationVisible(true);
    // Hide celebration after 5 seconds
    setTimeout(() => setCelebrationVisible(false), 5000);
  };

  // Close celebration modal
  const closeCelebration = () => {
    setCelebrationVisible(false);
    setSelectedBatch(null);
  };

  useEffect(() => {
    const fetchStudentAchievements = async () => {
      setLoading(true);
      setError(null);
      try {
        const user = localStorage.getItem('user');
        let parsedUser = null;
        try {
          parsedUser = JSON.parse(user);
        } catch (e) {
          console.error("Failed to parse user from localStorage:", e);
          setError("Failed to load user data. Please log in again.");
          setLoading(false);
          return;
        }

        if (!parsedUser || !parsedUser.id) {
          console.warn("User ID not found. Cannot fetch achievements.");
          setError("User not identified. Please log in.");
          setLoading(false);
          return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
          console.warn("Authentication token not found. Cannot fetch achievements.");
          setError("Authentication required. Please log in.");
          setLoading(false);
          return;
        }

        // --- API call for student achievements ---
        const response = await axios.get(`${baseUrl}api/students/achievment/${parsedUser.id}`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        // --- End of API call ---

        if (response.data) {
          setStudentData(response.data); // Set the full student data
          // Assuming API response structure includes 'unlockedBatches' and 'lockedBatches'
          // and that unlockedBatches items have 'dateAssigned' and 'milestone'
          const unlocked = response.data.unlockedBatches || [];
          const locked = response.data.lockedBatches || [];

          // For demonstration, if API returns empty, use dummy data
          if (unlocked.length === 0 && locked.length === 0) {
            setUnlockedBatches([
              { _id: 'unlocked1', batchId: 'batchA', name: 'React Explorer', description: 'Mastered React basics.', unlockedDate: '2023-01-15', dateAssigned: '2023-01-15', milestone: 50 },
              { _id: 'unlocked2', batchId: 'batchD', name: 'Python Pro', description: 'Completed advanced Python modules.', unlockedDate: '2023-03-20', dateAssigned: '2023-03-20', milestone: 100 },
              { _id: 'unlocked3', batchId: 'batchF', name: 'CSS Stylist', description: 'Styled responsive web pages.', unlockedDate: '2023-05-10', dateAssigned: '2023-05-10', milestone: 75 },
              { _id: 'unlocked4', batchId: 'batchG', name: 'JS Ninja', description: 'Conquered complex JavaScript algorithms.', unlockedDate: '2023-06-01', dateAssigned: '2023-06-01', milestone: 120 },
            ]);
            setLockedBatches([
              { _id: 'locked1', batchId: 'batchB', name: 'Algorithm Ace', description: 'Solve 50 LeetCode problems.' },
              { _id: 'locked2', batchId: 'batchC', name: 'Team Collaborator', description: 'Contribute to 5 open-source projects.' },
              { _id: 'locked3', batchId: 'batchE', name: 'Data Scientist', description: 'Complete the Data Science track.' },
              { _id: 'locked4', batchId: 'batchH', name: 'Cloud Architect', description: 'Pass AWS Certified Developer exam.' },
            ]);
          } else {
            setUnlockedBatches(unlocked);
            setLockedBatches(locked);
          }

          // Calculate overall progress based on total possible badges
          const totalPossibleBatches = (response.data.unlockedBatches?.length || 0) + (response.data.lockedBatches?.length || 0);
          if (totalPossibleBatches > 0) {
            setUserOverallProgress(((response.data.unlockedBatches?.length || 0) / totalPossibleBatches) * 100);
          } else {
            setUserOverallProgress(0);
          }
        }
      } catch (err) {
        console.error('Error fetching student achievements:', err);
        if (axios.isAxiosError(err) && err.response) {
          if (err.response.status === 401) {
            setError("Session expired or unauthorized. Please log in again.");
          } else if (err.response.status === 404) {
            setError("Achievements data not found for this user.");
          } else {
            setError(`Failed to fetch achievements: ${err.response.statusText || err.message}`);
          }
        } else {
          setError("Failed to fetch achievements. Please check your network connection.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStudentAchievements();
  }, []); // Empty dependency array as parsedUser.id is derived from localStorage and is static for the component's lifecycle

  // Effect for window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Loading state UI
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 font-sans p-6">
        <div className="flex flex-col items-center p-8 bg-white rounded-3xl shadow-xl">
          <div className="animate-bounce bg-blue-500 p-4 rounded-full mb-4">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>
          </div>
          <p className="text-xl text-gray-700 font-semibold">Loading super skill ...</p>
        </div>
      </div>
    );
  }
  // Error state UI
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white p-8">
        <div className="flex flex-col items-center text-red-700">
          <XCircleIcon className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-xl font-semibold">{error}</p>
          <p className="text-base text-red-600 mt-2">Please refresh the page or try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-inter relative overflow-hidden">
      {/* Subtle background gradient for a professional touch */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white opacity-50"></div>

      {/* Main content container with a clean, elevated look */}
      <div className="max-w-7xl mx-auto bg-white rounded-3xl shadow-2xl p-6 md:p-10 border border-gray-100 relative z-10">
        {/* Header Section */}
     <div className="mb-8 pb-4 border-b border-gray-200">
  <div>
    <h1 className="text-3xl font-extrabold text-gray-800 drop-shadow-sm flex items-center">
      <StarIcon className="w-10 h-10 mr-3 text-yellow-500 animate-spin-slow" />
      My Achievements
    </h1>

    <p className="text-lg text-gray-700  leading-relaxed max-w-3xl pl-[52px]">
      Earn Badges by completing milestones
    </p>
  </div>
</div>


       

        {/* Overall Progress Section */}
        <div className="mb-12 p-6 bg-gradient-to-r from-emerald-50 to-lime-50 rounded-2xl shadow-lg border border-emerald-100">
          <h2 className="text-2xl font-bold text-emerald-800 mb-4 flex items-center">
            <TrophyIcon className="w-8 h-8 mr-3 text-emerald-600" /> Your Grand Progress
          </h2>
          <div className="w-full bg-gray-200 rounded-full h-8 overflow-hidden shadow-inner">
            <div
              className="bg-gradient-to-r from-emerald-500 via-yellow-500 to-indigo-500 h-full rounded-full transition-all duration-1000 ease-out flex items-center justify-end pr-4 text-white text-lg font-bold"
              style={{ width: `${userOverallProgress}%` }}
            >
              {userOverallProgress.toFixed(0)}%
            </div>
          </div>
          <p className="text-center text-gray-600 text-sm mt-3">
            You've unlocked <span className="font-bold text-emerald-700">{unlockedBatches.length}</span> out of <span className="font-bold text-indigo-700">{unlockedBatches.length + lockedBatches.length}</span> available super skills! Every step counts!
          </p>
        </div>

        {/* Unlocked Achievements Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-emerald-800 mb-6 flex items-center">
            <SparklesIcon className="w-9 h-9 mr-3 text-yellow-500 animate-pulse-fast" /> Unlocked Super Skills
          </h2>
          {unlockedBatches.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {unlockedBatches.map((badge) => (
                <div
                  key={badge._id}
                  className="bg-white rounded-xl shadow-md p-5 border border-emerald-200 transform transition-transform duration-300 hover:scale-[1.03] hover:shadow-xl group relative overflow-hidden cursor-pointer"
                  onClick={() => handleBatchClick(badge)} // Make badge clickable
                >
                  {/* Subtle hover effect for unlocked badges */}
                  <div className="absolute inset-0 bg-emerald-50 opacity-0 group-hover:opacity-70 transition-opacity duration-300 rounded-xl"></div>
                  <div className="relative z-10 flex flex-col items-center text-center">
                    <div className="w-28 h-28 rounded-full bg-emerald-100 border-4 border-emerald-400 flex items-center justify-center mb-4 shadow-lg overflow-hidden transform group-hover:scale-105 transition-transform duration-200">
                      <img
                        src={getBatchImage(badge.batchId)}
                        alt={badge.name}
                        className="w-full h-full object-cover rounded-full"
                      />
                    </div>
                    <h3 className="text-xl font-bold text-emerald-700 mb-2">{badge.name}</h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{badge.description || 'No description available.'}</p>
                    <div className="flex items-center text-emerald-500 text-xs font-semibold mt-2">
                      <CheckCircleIcon className="w-4 h-4 mr-1" />
                      Awarded On: {formatDate(badge.dateAssigned)}
                    </div>
                    {badge.milestone && (
                      <p className="text-gray-600 text-xs mt-1">
                        Milestone: <span className="font-semibold">{badge.milestone} classes</span>
                      </p>
                    )}
                    {/* Share button with better styling */}
                    <button className="mt-4 px-5 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-full text-sm font-semibold hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 shadow-lg flex items-center">
                      <ShareIcon className="w-4 h-4 mr-2" /> Share Skill
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center bg-gray-50 rounded-xl border border-gray-200 shadow-inner">
              <p className="text-gray-600 text-lg italic">
                No super skills unlocked yet. Time to embark on your learning adventure!
              </p>
            </div>
          )}
        </div>

        {/* Locked Achievements Section */}
        <div>
          <h2 className="text-3xl font-bold text-indigo-800 mb-6 flex items-center">
            <LockClosedIcon className="w-9 h-9 mr-3 text-indigo-500" /> Locked Super Skills
          </h2>
          {lockedBatches.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {lockedBatches.map((badge) => (
                <div
                  key={badge._id}
                  className="bg-white rounded-xl shadow-md p-5 border border-indigo-200 transform transition-transform duration-300 hover:scale-[1.03] hover:shadow-xl group relative overflow-hidden cursor-pointer"
                >
                  {/* Subtle hover effect for locked badges */}
                  <div className="absolute inset-0 bg-indigo-50 opacity-0 group-hover:opacity-70 transition-opacity duration-300 rounded-xl"></div>
                  <div className="relative z-10 flex flex-col items-center text-center opacity-80 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-28 h-28 rounded-full bg-gray-100 border-4 border-indigo-300 flex items-center justify-center mb-4 shadow-lg overflow-hidden relative">
                      <img
                        src={getBatchImage(badge.batchId, true)} // Pass true for locked image
                        alt={badge.name}
                        className="w-full h-full object-cover rounded-full grayscale group-hover:grayscale-0 transition-all duration-300"
                      />
                      <LockClosedIcon className="absolute w-14 h-14 text-indigo-500 opacity-80 group-hover:opacity-0 transition-opacity duration-300" />
                    </div>
                    <h3 className="text-xl font-bold text-indigo-700 mb-2">{badge.name}</h3> {/* Using badge.name as title */}
                    <p className="text-gray-500 text-sm mb-3 line-clamp-2">{badge.description || 'No description available.'}</p>
                    <div className="flex items-center text-indigo-500 text-xs font-semibold mt-2">
                      <AcademicCapIcon className="w-4 h-4 mr-1" />
 Milestone:                    </div>
                        <span className="font-semibold">{badge.milestone} classes</span>
                    {/* Call to action button with better styling */}
                    {/* <button className="mt-4 px-5 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-full text-sm font-semibold hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 shadow-lg">
                      Start Challenge
                    </button> */}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center bg-gray-50 rounded-xl border border-gray-200 shadow-inner">
              <p className="text-gray-600 text-lg italic">
                All super skills unlocked! Congratulations, you are a true master!
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Celebration Modal */}
      {celebrationVisible && selectedBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full text-center shadow-2xl relative animate-scale-in border-4 border-yellow-400">
            <button
              onClick={closeCelebration}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition-colors"
            >
              <XMarkIcon className="w-8 h-8" />
            </button>
            <div className="flex flex-col items-center">
              <SparklesIcon className="w-24 h-24 text-yellow-400 mb-4 animate-bounce-celebration" />
              <h2 className="text-4xl font-extrabold text-emerald-700 mb-4 animate-fade-in-up">
                Skill Unlocked!
              </h2>
              <div className="w-40 h-40 rounded-full bg-emerald-100 border-6 border-emerald-500 flex items-center justify-center mb-6 shadow-xl overflow-hidden animate-zoom-in">
                <img
                  src={getBatchImage(selectedBatch.batchId)}
                  alt={selectedBatch.name}
                  className="w-full h-full object-cover rounded-full"
                />
              </div>
              <h3 className="text-3xl font-bold text-emerald-800 mb-2 animate-fade-in-up delay-100">{selectedBatch.name}</h3>
              <p className="text-gray-700 text-lg mb-4 animate-fade-in-up delay-200">{selectedBatch.description || 'A new skill mastered!'}</p>
              <div className="text-emerald-600 text-base font-semibold mb-2 animate-fade-in-up delay-300">
                Awarded On: {formatDate(selectedBatch.dateAssigned)}
              </div>
              {selectedBatch.milestone && (
                <p className="text-gray-600 text-base animate-fade-in-up delay-400">
                  Milestone: <span className="font-bold">{selectedBatch.milestone} classes</span>
                </p>
              )}
              <button
                onClick={closeCelebration}
                className="mt-8 px-8 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-full text-lg font-semibold hover:from-emerald-600 hover:to-emerald-700 transition-all duration-300 shadow-xl animate-fade-in-up delay-500"
              >
                Awesome!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tailwind CSS Custom Animations */}
      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse-fast {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounce-celebration {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        .animate-spin-slow {
          animation: spin-slow 10s linear infinite;
        }
        .animate-pulse-fast {
          animation: pulse-fast 1.5s infinite ease-in-out;
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out forwards;
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.5s ease-out forwards;
        }
        .animate-bounce-celebration {
          animation: bounce-celebration 1s infinite alternate;
        }
        .delay-100 { animation-delay: 0.1s; }
        .delay-200 { animation-delay: 0.2s; }
        .delay-300 { animation-delay: 0.3s; }
        .delay-400 { animation-delay: 0.4s; }
        .delay-500 { animation-delay: 0.5s; }
        .animate-zoom-in {
          animation: scale-in 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default AchievementsPage;
